// Composite Column Analysis Input Form Component
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Card, CardContent, CardHeader, Typography, TextField, Button,
  Grid, FormControl, InputLabel, Select, MenuItem, Checkbox,
  FormControlLabel, Divider, Alert, CircularProgress, Box, Tooltip,
  IconButton, RadioGroup, Radio
} from '@mui/material';
import { Engineering, Calculate, Save, FolderOpen, Download, Add, Delete } from '@mui/icons-material';
import axios from 'axios';
import CalculationHistory from '../shared/CalculationHistory';

// EN 1990 Action Categories for load input
const ACTION_CATEGORIES = [
  { value: 'permanent', label: 'Permanent' },
  { value: 'A', label: 'Imposed - Category A (residential)' },
  { value: 'B', label: 'Imposed - Category B (office)' },
  { value: 'C', label: 'Imposed - Category C (congregation)' },
  { value: 'D', label: 'Imposed - Category D (shopping)' },
  { value: 'E', label: 'Imposed - Category E (storage)' },
  { value: 'snow_low', label: 'Snow ≥ 2.75 kN/m²' },
  { value: 'snow_high', label: 'Snow < 2.75 kN/m²' },
  { value: 'wind', label: 'Wind' },
];

// EN 1990 ψ coefficients for load combinations
const EN1990_PSI_COEFFICIENTS = {
  'permanent': { psi0: 1.0, psi1: 1.0, psi2: 1.0 },
  'A': { psi0: 0.7, psi1: 0.5, psi2: 0.3 },
  'B': { psi0: 0.7, psi1: 0.5, psi2: 0.3 },
  'C': { psi0: 0.7, psi1: 0.7, psi2: 0.6 },
  'D': { psi0: 0.7, psi1: 0.7, psi2: 0.6 },
  'E': { psi0: 1.0, psi1: 0.9, psi2: 0.8 },
  'snow_low': { psi0: 0.7, psi1: 0.4, psi2: 0.2 },
  'snow_high': { psi0: 0.7, psi1: 0.5, psi2: 0.2 },
  'wind': { psi0: 0.6, psi1: 0.2, psi2: 0.0 },
};

// Advanced Load Input Component
const AdvancedLoadInput = ({ loadCases, setLoadCases, consequenceClass, setConsequenceClass, parseFlexibleFloat }) => {
  const addLoadCase = () => {
    const newLoadCase = {
      id: Date.now(),
      name: `Load Case ${loadCases.length + 1}`,
      permanent: {
        category: 'permanent',
        N: 500,
        My: 50,
        Mz: 25,
        comment: ''
      },
      imposed: {
        category: 'A',
        N: 300,
        My: 30,
        Mz: 15,
        comment: ''
      }
    };
    setLoadCases([...loadCases, newLoadCase]);
  };

  const removeLoadCase = (id) => {
    if (loadCases && loadCases.length > 1) {
      setLoadCases(loadCases.filter(lc => lc.id !== id));
    }
  };

  const updateLoadCase = (id, type, field, value) => {
    if (loadCases) {
      setLoadCases(loadCases.map(lc =>
        lc.id === id
          ? { ...lc, [type]: { ...lc[type], [field]: field === 'category' ? value : parseFlexibleFloat(value) } }
          : lc
      ));
    }
  };

  return (
    <div>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={6}>
          <FormControl fullWidth>
            <InputLabel>Consequence Class (EN 1990)</InputLabel>
            <Select
              value={consequenceClass}
              label="Consequence Class (EN 1990)"
              onChange={(e) => setConsequenceClass(e.target.value)}
            >
              <MenuItem value="CC2">CC2 - K_FI = 1.0 (normal consequences)</MenuItem>
              <MenuItem value="CC3">CC3 - K_FI = 1.1 (high consequences)</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={6}>
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', pl: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Current K_FI: <strong>{consequenceClass === 'CC3' ? '1.1' : '1.0'}</strong> on persistent ULS actions (not the fire length)
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={6}>
          <Button
            variant="outlined"
            startIcon={<Add />}
            onClick={addLoadCase}
            fullWidth
          >
            Add Load Case
          </Button>
        </Grid>
      </Grid>

      {loadCases && loadCases.map((loadCase) => (
        <Card key={loadCase.id} sx={{ mb: 2 }}>
          <CardHeader
            title={loadCase.name}
            action={
              loadCases.length > 1 && (
                <IconButton onClick={() => removeLoadCase(loadCase.id)} size="small">
                  <Delete />
                </IconButton>
              )
            }
          />
          <CardContent>
            <Typography variant="subtitle2" gutterBottom>Permanent Actions</Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Category</InputLabel>
                  <Select
                    value={loadCase.permanent.category}
                    label="Category"
                    onChange={(e) => updateLoadCase(loadCase.id, 'permanent', 'category', e.target.value)}
                  >
                    {ACTION_CATEGORIES.filter(cat => cat.value === 'permanent').map(cat => (
                      <MenuItem key={cat.value} value={cat.value}>{cat.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={3}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label="N (kN)"
                  value={loadCase.permanent.N}
                  onChange={(e) => updateLoadCase(loadCase.id, 'permanent', 'N', e.target.value)}
                />
              </Grid>
              <Grid item xs={3}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label="My (kNm)"
                  value={loadCase.permanent.My}
                  onChange={(e) => updateLoadCase(loadCase.id, 'permanent', 'My', e.target.value)}
                />
              </Grid>
              <Grid item xs={3}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label="Mz (kNm)"
                  value={loadCase.permanent.Mz}
                  onChange={(e) => updateLoadCase(loadCase.id, 'permanent', 'Mz', e.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label="Comment"
                  value={loadCase.permanent.comment}
                  onChange={(e) => updateLoadCase(loadCase.id, 'permanent', 'comment', e.target.value)}
                />
              </Grid>
            </Grid>

            <Typography variant="subtitle2" gutterBottom>Variable Actions</Typography>
            <Grid container spacing={2}>
              <Grid item xs={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Category</InputLabel>
                  <Select
                    value={loadCase.imposed.category}
                    label="Category"
                    onChange={(e) => updateLoadCase(loadCase.id, 'imposed', 'category', e.target.value)}
                  >
                    {ACTION_CATEGORIES.filter(cat => cat.value !== 'permanent').map(cat => (
                      <MenuItem key={cat.value} value={cat.value}>{cat.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={3}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label="N (kN)"
                  value={loadCase.imposed.N}
                  onChange={(e) => updateLoadCase(loadCase.id, 'imposed', 'N', e.target.value)}
                />
              </Grid>
              <Grid item xs={3}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label="My (kNm)"
                  value={loadCase.imposed.My}
                  onChange={(e) => updateLoadCase(loadCase.id, 'imposed', 'My', e.target.value)}
                />
              </Grid>
              <Grid item xs={3}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label="Mz (kNm)"
                  value={loadCase.imposed.Mz}
                  onChange={(e) => updateLoadCase(loadCase.id, 'imposed', 'Mz', e.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label="Comment"
                  value={loadCase.imposed.comment}
                  onChange={(e) => updateLoadCase(loadCase.id, 'imposed', 'comment', e.target.value)}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

// TODO: Re-implement AdvancedLoadInput component after fixing syntax issues

const CompositeColumnInputForm = ({ onAnalysisComplete }) => {
  console.log('=== CompositeColumnInputForm COMPONENT RENDERED ===');
  const [searchParams, setSearchParams] = useSearchParams();
  const historyRef = useRef(null);

  // Helper to parse float with either comma or dot
  const parseFlexibleFloat = (value) => {
    if (value === null || value === undefined || value === '') return 0;
    const str = String(value).replace(',', '.');
    const val = parseFloat(str);
    return isNaN(val) ? 0 : val;
  };

  // Calculate rebar ratio
  const calculateRebarRatio = (config) => {
    const numBars = config.comcol_num_bars || 0;
    const barDiameter = config.comcol_bar_diameter || 0;

    if (numBars <= 0 || barDiameter <= 0) return 0;

    // Calculate rebar area
    const barArea = numBars * Math.PI * Math.pow(barDiameter / 2, 2);

    // Estimate concrete area based on section type
    let concreteArea = 0;
    if (config.comcol_section_type === 'CHS') {
      const D = config.comcol_diameter_height || 0;
      const t = config.comcol_thickness || 0;
      if (D > 0 && t > 0) {
        const D_i = D - 2 * t;
        concreteArea = (Math.PI / 4) * (D * D - D_i * D_i);
      }
    } else if (config.comcol_section_type === 'RHS') {
      const H = config.comcol_diameter_height || 0;
      const W = config.comcol_width || 0;
      const t = config.comcol_thickness || 0;
      if (H > 0 && W > 0 && t > 0) {
        concreteArea = H * W - (H - 2 * t) * (W - 2 * t);
      }
    } else if (['H', 'I', 'HEA', 'HEB', 'HEM', 'IPE'].includes(config.comcol_section_type)) {
      const H = config.comcol_beam_height || 0;
      const W = config.comcol_flange_width || 0;
      const t_f = config.comcol_flange_thickness || 0;
      const t_w = config.comcol_web_thickness || 0;
      if (H > 0 && W > 0 && t_f > 0 && t_w > 0) {
        concreteArea = H * W - (H - 2 * t_f) * (W - 2 * t_w);
      }
    }

    return concreteArea > 0 ? (barArea / concreteArea) * 100 : 0;
  };

  // Helper functions for data persistence (defined before state to avoid temporal dead zone)
  const saveFormData = (data) => {
    try {
      localStorage.setItem('compositeColumnFormData', JSON.stringify(data));
    } catch (e) {
      console.warn('Failed to save form data to localStorage:', e);
    }
  };

  const loadFormData = () => {
    try {
      const data = localStorage.getItem('compositeColumnFormData');
      return data ? JSON.parse(data) : null;
    } catch (e) {
      console.warn('Failed to load form data from localStorage:', e);
      return null;
    }
  };

  // State management
  const [projectData, setProjectData] = useState(() => {
    const saved = loadFormData();
    return saved?.projectData || {
      name: '',
      description: '',
      fire_design_enabled: false,
    };
  });

  const [sectionConfig, setSectionConfig] = useState(() => {
    const saved = loadFormData();
    return saved?.sectionConfig || {
      column_type: 'concrete_filled_tubular',
      comcol_length: 6000,
      L_eff_y: '',
      L_eff_z: '',
      optimize_rebar: false,
      comcol_concrete_age: 28,
      comcol_section_type: 'CHS',
      comcol_section_name: '323.9x10.0',
      comcol_diameter_height: 323.9,
      comcol_width: 323.9,
      comcol_thickness: 10.0,
      comcol_corner_radius: 0,
      comcol_num_bars: 4,
      comcol_bar_diameter: 20,
      comcol_cover: 40,
      comcol_concrete_grade: 'C30/37',
      comcol_steel_grade: 'S355',
      comcol_rebar_grade: 'B500B',
    };
  });

  const [loads, setLoads] = useState(() => {
    const saved = loadFormData();
    return saved?.loads || {
      N_Ed: 1000,
      M_Ed_y: 100,
      M_Ed_z: 50,
    };
  });

  const [fireSettings, setFireSettings] = useState(() => {
    const saved = loadFormData();
    return saved?.fireSettings || {
      required_rating: 30,
      load_factor_fi: 0.65,
      k_L_modifier: 1.0,
      beta_y: '',
      beta_z: '',
      fire_temperature_source: 'ncci_tables',
      thermal_fem_element_size_mm: 14,
      thermal_fem_dt_seconds: 30,
      thermal_fem_wall_aligned_mesh: true,
    };
  });

  // State for advanced load input
  const [useAdvancedLoads, setUseAdvancedLoads] = useState(false);
  const [consequenceClass, setConsequenceClass] = useState('CC2');
  const [loadCases, setLoadCases] = useState([]);

  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [results, setResults] = useState(null);

  const getCompositeSavePayload = useCallback(() => ({
    name: `${projectData.name || 'Composite column'} — ${new Date().toLocaleString()}`,
    description: projectData.description || '',
    input_data: {
      projectData,
      sectionConfig,
      loads,
      fireSettings,
      useAdvancedLoads,
      consequenceClass,
      loadCases,
    },
    results_data: results,
    comcol_column_type: sectionConfig.column_type || 'concrete_filled_tubular',
    comcol_section_name: sectionConfig.comcol_section_name || '',
  }), [
    projectData,
    sectionConfig,
    loads,
    fireSettings,
    useAdvancedLoads,
    consequenceClass,
    loadCases,
    results,
  ]);

  useEffect(() => {
    const sid = searchParams.get('calcSession');
    if (!sid) return;
    let cancelled = false;
    axios
      .get(`/api/composite-columns/saved-analyses/${sid}/`)
      .then((r) => {
        if (cancelled) return;
        const d = r.data;
        const inp = d.input_data || {};
        if (inp.projectData) setProjectData(inp.projectData);
        if (inp.sectionConfig) setSectionConfig(inp.sectionConfig);
        if (inp.loads) setLoads(inp.loads);
        if (inp.fireSettings) setFireSettings(inp.fireSettings);
        if (inp.useAdvancedLoads != null) setUseAdvancedLoads(inp.useAdvancedLoads);
        if (inp.consequenceClass) setConsequenceClass(inp.consequenceClass);
        if (inp.loadCases) setLoadCases(inp.loadCases);
        if (d.results_data) {
          setResults(d.results_data);
          onAnalysisComplete?.(d.results_data);
        }
        setSearchParams({}, { replace: true });
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  // Auto-generate Load Case 1 when advanced loads are enabled
  useEffect(() => {
    if (useAdvancedLoads && loadCases.length === 0) {
      const newLoadCase = {
        id: Date.now(),
        name: `Load Case 1`,
        permanent: {
          category: 'permanent',
          N: 500,
          My: 50,
          Mz: 25,
          comment: ''
        },
        imposed: {
          category: 'A',
          N: 300,
          My: 30,
          Mz: 15,
          comment: ''
        }
      };
      setLoadCases([newLoadCase]);
    }
  }, [useAdvancedLoads]);

  // Save form data when it changes
  useEffect(() => {
    saveFormData({
      projectData,
      sectionConfig,
      loads,
      fireSettings,
      useAdvancedLoads,
      consequenceClass,
      loadCases,
    });
  }, [projectData, sectionConfig, loads, fireSettings, useAdvancedLoads, consequenceClass, loadCases]);

  // Update rebar ratio when relevant parameters change
  useEffect(() => {
    const ratio = calculateRebarRatio(sectionConfig);
    if (ratio !== sectionConfig.comcol_rebar_ratio) {
      setSectionConfig(prev => ({ ...prev, comcol_rebar_ratio: ratio }));
    }
  }, [sectionConfig.comcol_num_bars, sectionConfig.comcol_bar_diameter, sectionConfig.comcol_section_type, sectionConfig.comcol_diameter_height, sectionConfig.comcol_width, sectionConfig.comcol_thickness]);

  const getSectionTypes = (columnType) => {
    if (columnType === 'concrete_filled_tubular') {
      return [
        { name: 'CHS', label: 'Circular Hollow Section (CHS)' },
        { name: 'RHS', label: 'Rectangular Hollow Section (RHS)' },
      ];
    } else if (columnType === 'concrete_covered') {
      return [
        { name: 'H', label: 'H Section' },
        { name: 'I', label: 'I Section' },
        { name: 'HEA', label: 'HEA Section' },
        { name: 'HEB', label: 'HEB Section' },
        { name: 'HEM', label: 'HEM Section' },
        { name: 'IPE', label: 'IPE Section' },
      ];
    }
    return [];
  };

  const calculateCornerRadius = (thickness) => {
    if (sectionConfig.comcol_section_type === 'CHS') {
      return 0;
    }
    return 3 * thickness;
  };

  const updateSectionConfig = (field, value) => {
    setSectionConfig(prev => {
      const newConfig = { ...prev, [field]: value };

      if (field === 'comcol_thickness') {
        newConfig.comcol_corner_radius = calculateCornerRadius(value);
      }

      return newConfig;
    });
  };

  const validateInputs = () => {
    const errors = [];

    if (!projectData.name.trim()) {
      errors.push("Project name is required");
    }

    if (sectionConfig.comcol_length < 1000 || sectionConfig.comcol_length > 20000) {
      errors.push("Column length must be between 1m and 20m");
    }

    if (sectionConfig.comcol_section_type === 'CHS' && (!sectionConfig.comcol_diameter_height || sectionConfig.comcol_diameter_height < 100)) {
      errors.push("CHS diameter must be at least 100mm");
    }

    if (sectionConfig.comcol_section_type === 'RHS' && (!sectionConfig.comcol_width || !sectionConfig.comcol_diameter_height || sectionConfig.comcol_width < 50 || sectionConfig.comcol_diameter_height < 50)) {
      errors.push("RHS dimensions must be at least 50mm");
    }

    if (sectionConfig.comcol_thickness < 3) {
      errors.push("Wall thickness must be at least 3mm");
    }

    if (!useAdvancedLoads) {
      if (loads.N_Ed <= 0) {
        errors.push("Axial load must be positive");
      }
    } else {
      if (!loadCases || loadCases.length === 0) {
        errors.push("At least one load case is required");
      } else {
        loadCases.forEach((loadCase, index) => {
          const caseNum = index + 1;

          if (loadCase.permanent.N < 0) {
            errors.push(`Load Case ${caseNum}: Permanent axial load cannot be negative`);
          }

          if (loadCase.imposed.N < 0) {
            errors.push(`Load Case ${caseNum}: Imposed axial load cannot be negative`);
          }

          const totalN = loadCase.permanent.N + loadCase.imposed.N;
          if (totalN <= 0) {
            errors.push(`Load Case ${caseNum}: Total axial load must be positive`);
          }
        });
      }
    }

    return errors;
  };

  // Calculate worst linear combination using EN 1990 rules
  const calculateWorstLinearCombination = (loadCases) => {
    if (!loadCases || loadCases.length === 0) {
      return {
        loads: { N_Ed: 0, M_Ed_y: 0, M_Ed_z: 0 },
        combination: "No load cases defined",
        details: {}
      };
    }

    let worstCombination = { N_Ed: 0, M_Ed_y: 0, M_Ed_z: 0 };
    let maxImportance = 0;
    let bestCombinationDetails = {};
    let bestCombinationDescription = "";

    // For each load case, try different combinations
    loadCases.forEach(loadCase => {
      const permanent = loadCase.permanent;
      const imposed = loadCase.imposed;

      // Get ψ coefficients
      const permanentPsi = EN1990_PSI_COEFFICIENTS[permanent.category] || EN1990_PSI_COEFFICIENTS['permanent'];
      const imposedPsi = EN1990_PSI_COEFFICIENTS[imposed.category] || EN1990_PSI_COEFFICIENTS['A'];

      // Try different combination factors (ψ0 for ultimate limit state)
      const combinations = [
        // Combination 1: Permanent + ψ0 × Imposed
        {
          N_Ed: permanent.N + imposedPsi.psi0 * imposed.N,
          M_Ed_y: permanent.My + imposedPsi.psi0 * imposed.My,
          M_Ed_z: permanent.Mz + imposedPsi.psi0 * imposed.Mz
        },
        // Combination 2: ψ0 × Permanent + Imposed
        {
          N_Ed: permanentPsi.psi0 * permanent.N + imposed.N,
          M_Ed_y: permanentPsi.psi0 * permanent.My + imposed.My,
          M_Ed_z: permanentPsi.psi0 * permanent.Mz + imposed.Mz
        },
        // Combination 3: Permanent only (if imposed is small)
        {
          N_Ed: permanent.N,
          M_Ed_y: permanent.My,
          M_Ed_z: permanent.Mz
        },
        // Combination 4: Imposed only (if permanent is small)
        {
          N_Ed: imposed.N,
          M_Ed_y: imposed.My,
          M_Ed_z: imposed.Mz
        }
      ];

      // Evaluate each combination
      combinations.forEach((combination, comboIndex) => {
        // Calculate importance score: 1*N_ED + 10*M_ED (M is 10x more important)
        const importance = Math.abs(combination.N_Ed) + 10 * (Math.abs(combination.M_Ed_y) + Math.abs(combination.M_Ed_z));

        if (importance > maxImportance) {
          maxImportance = importance;
          worstCombination = { ...combination };

          // Create combination description
          const permanentDesc = permanent.category === 'permanent' ?
            `Permanent (G)` :
            `${permanent.category} (ψ=${permanentPsi.psi0})`;

          const imposedDesc = imposed.category === 'permanent' ?
            `Permanent (G)` :
            `${imposed.category} (ψ=${imposedPsi.psi0})`;

          const comboType = comboIndex === 0 ? "G + ψ×Q" : comboIndex === 1 ? "ψ×G + Q" : comboIndex === 2 ? "G only" : "Q only";

          bestCombinationDescription = `${comboType}: ${permanentDesc} + ${imposedDesc}`;

          bestCombinationDetails = {
            type: comboType,
            permanent: {
              category: permanent.category,
              psi: permanentPsi.psi0,
              loads: permanent
            },
            imposed: {
              category: imposed.category,
              psi: imposedPsi.psi0,
              loads: imposed
            },
            combined: combination
          };
        }
      });
    });

    return {
      loads: worstCombination,
      combination: bestCombinationDescription,
      details: bestCombinationDetails
    };
  };

  const handleRunAnalysis = async () => {
    console.log('=== COMPOSITE COLUMN ANALYSIS CALLED ===');
    setLoading(true);
    setError(null);
    setResults(null);

    const validationErrors = validateInputs();
    if (validationErrors.length > 0) {
      setError(validationErrors.join('\n• '));
      setLoading(false);
      return;
    }

    try {
      const projectResponse = await axios.post('/api/composite-columns/', projectData);
      const projectId = projectResponse.data.id;

      const analysisInput = {
        project_name: projectData.name,
        project_description: projectData.description,
        column_type: sectionConfig.column_type,
        comcol_length: sectionConfig.comcol_length,
        ...(sectionConfig.L_eff_y ? { L_eff_y: sectionConfig.L_eff_y } : {}),
        ...(sectionConfig.L_eff_z ? { L_eff_z: sectionConfig.L_eff_z } : {}),
        optimize_rebar: Boolean(sectionConfig.optimize_rebar)
          && ['CHS', 'RHS'].includes(sectionConfig.comcol_section_type),
        comcol_section_type: sectionConfig.comcol_section_type,
        comcol_section_name: sectionConfig.comcol_section_name,
        comcol_num_bars: sectionConfig.comcol_num_bars,
        comcol_bar_diameter: sectionConfig.comcol_bar_diameter,
        comcol_cover: sectionConfig.comcol_cover,
        comcol_concrete_grade: sectionConfig.comcol_concrete_grade,
        comcol_steel_grade: sectionConfig.comcol_steel_grade,
        comcol_rebar_grade: sectionConfig.comcol_rebar_grade,
        fire_design_enabled: projectData.fire_design_enabled,
        required_rating: fireSettings.required_rating,
        load_factor_fi: fireSettings.load_factor_fi,
        k_L_modifier: fireSettings.k_L_modifier ?? 1.0,
        ...(fireSettings.beta_y !== '' && fireSettings.beta_y != null ? { beta_y: fireSettings.beta_y } : {}),
        ...(fireSettings.beta_z !== '' && fireSettings.beta_z != null ? { beta_z: fireSettings.beta_z } : {}),
        fire_temperature_source: fireSettings.fire_temperature_source || 'ncci_tables',
        thermal_fem_element_size_mm: fireSettings.thermal_fem_element_size_mm ?? 14,
        thermal_fem_dt_seconds: fireSettings.thermal_fem_dt_seconds ?? 30,
        thermal_fem_wall_aligned_mesh: fireSettings.thermal_fem_wall_aligned_mesh !== false,
      };

      if (useAdvancedLoads) {
        analysisInput.use_advanced_loads = true;
        analysisInput.consequence_class = consequenceClass;
        analysisInput.load_cases = loadCases;

        // Calculate the worst linear combination for analysis
        const combinationResult = calculateWorstLinearCombination(loadCases);
        // Apply consequence class factor (K_FI) to the combined loads
        const k_fi = consequenceClass === 'CC2' ? 1.0 : 1.1;
        const N_Ed_final = combinationResult.loads.N_Ed * k_fi;
        const M_Ed_y_final = combinationResult.loads.M_Ed_y * k_fi;
        const M_Ed_z_final = combinationResult.loads.M_Ed_z * k_fi;

        analysisInput.N_Ed = N_Ed_final;
        analysisInput.M_Ed_y = M_Ed_y_final;
        analysisInput.M_Ed_z = M_Ed_z_final;
        analysisInput.linear_combination = {
          N_Ed: N_Ed_final,
          M_Ed_y: M_Ed_y_final,
          M_Ed_z: M_Ed_z_final
        };
        analysisInput.combination_description = combinationResult.combination;
        analysisInput.combination_details = combinationResult.details;
      } else {
        analysisInput.use_advanced_loads = false;
        analysisInput.N_Ed = loads.N_Ed;
        analysisInput.M_Ed_y = loads.M_Ed_y;
        analysisInput.M_Ed_z = loads.M_Ed_z;
      }

      if (sectionConfig.comcol_section_type === 'CHS') {
        analysisInput.comcol_diameter_height = sectionConfig.comcol_diameter_height;
        analysisInput.comcol_thickness = sectionConfig.comcol_thickness;
      } else if (sectionConfig.comcol_section_type === 'RHS') {
        analysisInput.comcol_diameter_height = sectionConfig.comcol_diameter_height;
        analysisInput.comcol_width = sectionConfig.comcol_width;
        analysisInput.comcol_thickness = sectionConfig.comcol_thickness;
        analysisInput.comcol_corner_radius = sectionConfig.comcol_corner_radius || 0;
      } else if (['H', 'I', 'HEA', 'HEB', 'HEM', 'IPE'].includes(sectionConfig.comcol_section_type)) {
        analysisInput.comcol_beam_height = sectionConfig.comcol_beam_height;
        analysisInput.comcol_flange_width = sectionConfig.comcol_flange_width;
        analysisInput.comcol_web_thickness = sectionConfig.comcol_web_thickness;
        analysisInput.comcol_flange_thickness = sectionConfig.comcol_flange_thickness;
        analysisInput.comcol_corner_radius = sectionConfig.comcol_corner_radius || 0;
      }

      console.log('Analysis input:', analysisInput);

      const analysisResponse = await axios.post(
        `/api/composite-columns/${projectId}/analyze/`,
        analysisInput
      );

      setResults(analysisResponse.data);

      if (onAnalysisComplete) {
        onAnalysisComplete(analysisResponse.data);
      }

      historyRef.current?.saveAuto?.({ results_data: analysisResponse.data });

    } catch (err) {
      console.error('Analysis error:', err);
      const errorMessage = err.response?.data?.error ||
                          err.response?.data?.message ||
                          err.message ||
                          'Analysis failed. Please check your inputs.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const getKFiValue = (cc) => {
    return cc === 'CC3' ? '1.1' : '1.0';
  };

  const addLoadCase = () => {
    const newLoadCase = {
      id: Date.now(),
      name: `Load Case ${loadCases.length + 1}`,
      permanent: {
        category: 'permanent',
        N: 500,
        My: 50,
        Mz: 25,
        comment: ''
      },
      imposed: {
        category: 'A',
        N: 300,
        My: 30,
        Mz: 15,
        comment: ''
      }
    };
    setLoadCases([...loadCases, newLoadCase]);
  };

  const removeLoadCase = (id) => {
    if (loadCases && loadCases.length > 1) {
      setLoadCases(loadCases.filter(lc => lc.id !== id));
    }
  };

  const updateLoadCase = (id, type, field, value) => {
    if (loadCases) {
      setLoadCases(loadCases.map(lc =>
        lc.id === id
          ? { ...lc, [type]: { ...lc[type], [field]: field === 'category' ? value : parseFlexibleFloat(value) } }
          : lc
      ));
    }
  };

  return (
    <div>
      <Typography variant="h4" component="h1" gutterBottom sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Engineering />
          Composite Column Analysis Tool
        </Box>
        <CalculationHistory
          ref={historyRef}
          apiEndpoint="/api/composite-columns/saved-analyses"
          getSavePayload={getCompositeSavePayload}
          appLabel="Composite column"
          onLoad={({ input_data, results_data }) => {
            const inp = input_data || {};
            if (inp.projectData) setProjectData(inp.projectData);
            if (inp.sectionConfig) setSectionConfig(inp.sectionConfig);
            if (inp.loads) setLoads(inp.loads);
            if (inp.fireSettings) setFireSettings(inp.fireSettings);
            if (inp.useAdvancedLoads != null) setUseAdvancedLoads(inp.useAdvancedLoads);
            if (inp.consequenceClass) setConsequenceClass(inp.consequenceClass);
            if (inp.loadCases) setLoadCases(inp.loadCases);
            if (results_data) {
              setResults(results_data);
              onAnalysisComplete?.(results_data);
            }
          }}
          renderSummary={(item) => (
            <Typography variant="caption" color="text.secondary">
              {[item.comcol_section_name, item.comcol_column_type].filter(Boolean).join(' · ') || '—'}
            </Typography>
          )}
        />
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader title="Project Configuration" />
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Project Name"
                    value={projectData.name}
                    onChange={(e) => setProjectData({...projectData, name: e.target.value})}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Project Description"
                    multiline
                    rows={2}
                    value={projectData.description}
                    onChange={(e) => setProjectData({...projectData, description: e.target.value})}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={projectData.fire_design_enabled}
                        onChange={(e) => setProjectData({...projectData, fire_design_enabled: e.target.checked})}
                      />
                    }
                    label="Enable Fire Design Analysis"
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader title="Section Configuration" />
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Column Type</InputLabel>
                    <Select
                      value={sectionConfig.column_type}
                      label="Column Type"
                      onChange={(e) => updateSectionConfig('column_type', e.target.value)}
                    >
                      <MenuItem value="concrete_filled_tubular">Concrete Filled Tubular</MenuItem>
                      <MenuItem value="concrete_covered">Concrete Covered</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Section Type</InputLabel>
                    <Select
                      value={sectionConfig.comcol_section_type}
                      label="Section Type"
                      onChange={(e) => updateSectionConfig('comcol_section_type', e.target.value)}
                    >
                      {getSectionTypes(sectionConfig.column_type).map(type => (
                        <MenuItem key={type.name} value={type.name}>{type.label}</MenuItem>
                      ))}
                      <MenuItem value="custom">Custom Section</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Column Length (mm)"
                    type="number"
                    value={sectionConfig.comcol_length}
                    onChange={(e) => updateSectionConfig('comcol_length', parseFlexibleFloat(e.target.value))}
                  />
                </Grid>

                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Buckling length L_eff,y (mm)"
                    type="number"
                    value={sectionConfig.L_eff_y ?? ''}
                    placeholder={String(sectionConfig.comcol_length || '')}
                    onChange={(e) => updateSectionConfig('L_eff_y', e.target.value === '' ? '' : parseFlexibleFloat(e.target.value))}
                    helperText="Blank uses the column length"
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Buckling length L_eff,z (mm)"
                    type="number"
                    value={sectionConfig.L_eff_z ?? ''}
                    placeholder={String(sectionConfig.comcol_length || '')}
                    onChange={(e) => updateSectionConfig('L_eff_z', e.target.value === '' ? '' : parseFlexibleFloat(e.target.value))}
                    helperText="Blank uses the column length"
                  />
                </Grid>

                {sectionConfig.comcol_section_type === 'CHS' && (
                  <>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        label="Diameter (mm)"
                        type="number"
                        value={sectionConfig.comcol_diameter_height}
                        onChange={(e) => updateSectionConfig('comcol_diameter_height', parseFlexibleFloat(e.target.value))}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        label="Thickness (mm)"
                        type="number"
                        value={sectionConfig.comcol_thickness}
                        onChange={(e) => updateSectionConfig('comcol_thickness', parseFlexibleFloat(e.target.value))}
                      />
                    </Grid>
                  </>
                )}

                {sectionConfig.comcol_section_type === 'RHS' && (
                  <>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        label="Height (mm)"
                        type="number"
                        value={sectionConfig.comcol_diameter_height}
                        onChange={(e) => updateSectionConfig('comcol_diameter_height', parseFlexibleFloat(e.target.value))}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        label="Width (mm)"
                        type="number"
                        value={sectionConfig.comcol_width}
                        onChange={(e) => updateSectionConfig('comcol_width', parseFlexibleFloat(e.target.value))}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        label="Thickness (mm)"
                        type="number"
                        value={sectionConfig.comcol_thickness}
                        onChange={(e) => updateSectionConfig('comcol_thickness', parseFlexibleFloat(e.target.value))}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        label="Corner Radius (mm)"
                        type="number"
                        value={sectionConfig.comcol_corner_radius}
                        onChange={(e) => updateSectionConfig('comcol_corner_radius', parseFlexibleFloat(e.target.value))}
                      />
                    </Grid>
                  </>
                )}

                {['H', 'I', 'HEA', 'HEB', 'HEM', 'IPE'].includes(sectionConfig.comcol_section_type) && (
                  <>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        label="Beam Height (mm)"
                        type="number"
                        value={sectionConfig.comcol_beam_height}
                        onChange={(e) => updateSectionConfig('comcol_beam_height', parseFlexibleFloat(e.target.value))}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        label="Flange Width (mm)"
                        type="number"
                        value={sectionConfig.comcol_flange_width}
                        onChange={(e) => updateSectionConfig('comcol_flange_width', parseFlexibleFloat(e.target.value))}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        label="Web Thickness (mm)"
                        type="number"
                        value={sectionConfig.comcol_web_thickness}
                        onChange={(e) => updateSectionConfig('comcol_web_thickness', parseFlexibleFloat(e.target.value))}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        label="Flange Thickness (mm)"
                        type="number"
                        value={sectionConfig.comcol_flange_thickness}
                        onChange={(e) => updateSectionConfig('comcol_flange_thickness', parseFlexibleFloat(e.target.value))}
                      />
                    </Grid>
                  </>
                )}

                <Grid item xs={6}>
                  <FormControl fullWidth>
                    <InputLabel>Concrete Grade</InputLabel>
                    <Select
                      value={sectionConfig.comcol_concrete_grade}
                      label="Concrete Grade"
                      onChange={(e) => updateSectionConfig('comcol_concrete_grade', e.target.value)}
                    >
                      <MenuItem value="C20/25">C20/25</MenuItem>
                      <MenuItem value="C25/30">C25/30</MenuItem>
                      <MenuItem value="C30/37">C30/37</MenuItem>
                      <MenuItem value="C35/45">C35/45</MenuItem>
                      <MenuItem value="C40/50">C40/50</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={6}>
                  <FormControl fullWidth>
                    <InputLabel>Steel Grade</InputLabel>
                    <Select
                      value={sectionConfig.comcol_steel_grade}
                      label="Steel Grade"
                      onChange={(e) => updateSectionConfig('comcol_steel_grade', e.target.value)}
                    >
                      <MenuItem value="S235">S235</MenuItem>
                      <MenuItem value="S275">S275</MenuItem>
                      <MenuItem value="S355">S355</MenuItem>
                      <MenuItem value="S420">S420</MenuItem>
                      <MenuItem value="S460">S460</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Number of Bars"
                    type="number"
                    value={sectionConfig.comcol_num_bars}
                    onChange={(e) => updateSectionConfig('comcol_num_bars', parseFlexibleFloat(e.target.value))}
                  />
                </Grid>

                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Bar Diameter (mm)"
                    type="number"
                    value={sectionConfig.comcol_bar_diameter}
                    onChange={(e) => updateSectionConfig('comcol_bar_diameter', parseFlexibleFloat(e.target.value))}
                  />
                </Grid>

                <Grid item xs={6}>
                  <FormControl fullWidth>
                    <InputLabel>Rebar Grade</InputLabel>
                    <Select
                      value={sectionConfig.comcol_rebar_grade}
                      label="Rebar Grade"
                      onChange={(e) => updateSectionConfig('comcol_rebar_grade', e.target.value)}
                    >
                      <MenuItem value="B500A">B500A</MenuItem>
                      <MenuItem value="B500B">B500B</MenuItem>
                      <MenuItem value="B500C">B500C</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Minimum concrete cover (mm)"
                    type="number"
                    value={sectionConfig.comcol_cover}
                    onChange={(e) => updateSectionConfig('comcol_cover', parseFlexibleFloat(e.target.value))}
                    helperText="Clear cover to the inner steel face. The optimizer may place bars deeper."
                  />
                </Grid>
                {['CHS', 'RHS'].includes(sectionConfig.comcol_section_type) && (
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={Boolean(sectionConfig.optimize_rebar)}
                          onChange={(e) => updateSectionConfig('optimize_rebar', e.target.checked)}
                        />
                      }
                      label="Optimize bar positions for the imposed loads and buckling lengths"
                    />
                    <Typography variant="caption" color="text.secondary" display="block">
                      Layouts stay symmetric about at least one axis. Embedment follows the selected fire rating. Minimum cover and bar spacing still apply.
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card>
            <CardHeader title="Load Configuration" />
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={useAdvancedLoads}
                        onChange={(e) => setUseAdvancedLoads(e.target.checked)}
                      />
                    }
                    label="Use Advanced Load Input"
                  />
                </Grid>

                {!useAdvancedLoads ? (
                  <>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        label="Axial Load N_Ed (kN)"
                        type="number"
                        value={loads.N_Ed}
                        onChange={(e) => setLoads({...loads, N_Ed: parseFlexibleFloat(e.target.value)})}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        label="Moment M_Ed,y (kNm)"
                        type="number"
                        value={loads.M_Ed_y}
                        onChange={(e) => setLoads({...loads, M_Ed_y: parseFlexibleFloat(e.target.value)})}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        label="Moment M_Ed,z (kNm)"
                        type="number"
                        value={loads.M_Ed_z}
                        onChange={(e) => setLoads({...loads, M_Ed_z: parseFlexibleFloat(e.target.value)})}
                      />
                    </Grid>
                  </>
                ) : (
                  <Grid item xs={12}>
                    <AdvancedLoadInput
                      loadCases={loadCases}
                      setLoadCases={setLoadCases}
                      consequenceClass={consequenceClass}
                      setConsequenceClass={setConsequenceClass}
                      parseFlexibleFloat={parseFlexibleFloat}
                    />
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {projectData.fire_design_enabled && (
          <Grid item xs={12} md={6}>
            <Card>
              <CardHeader title="Fire Design Configuration" />
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <FormControl fullWidth>
                      <InputLabel>Required fire rating</InputLabel>
                      <Select
                        value={[30, 60, 90, 120].includes(Number(fireSettings.required_rating))
                          ? Number(fireSettings.required_rating)
                          : 60}
                        label="Required fire rating"
                        onChange={(e) => setFireSettings({
                          ...fireSettings,
                          required_rating: Number(e.target.value),
                        })}
                      >
                        <MenuItem value={30}>R30</MenuItem>
                        <MenuItem value={60}>R60</MenuItem>
                        <MenuItem value={90}>R90</MenuItem>
                        <MenuItem value={120}>R120</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="Load Factor η_fi"
                      type="number"
                      step="0.01"
                      value={fireSettings.load_factor_fi}
                      onChange={(e) => setFireSettings({...fireSettings, load_factor_fi: parseFlexibleFloat(e.target.value)})}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <FormControl fullWidth>
                      <InputLabel>β_y (NCCI 1 Table 9)</InputLabel>
                      <Select
                        value={fireSettings.beta_y === '' || fireSettings.beta_y == null ? 'same' : String(fireSettings.beta_y)}
                        label="β_y (NCCI 1 Table 9)"
                        onChange={(e) => setFireSettings({
                          ...fireSettings,
                          beta_y: e.target.value === 'same' ? '' : parseFloat(e.target.value),
                        })}
                      >
                        <MenuItem value="same">Same as β</MenuItem>
                        <MenuItem value="1">1.0</MenuItem>
                        <MenuItem value="0.7">0.7</MenuItem>
                        <MenuItem value="0.6">0.6</MenuItem>
                        <MenuItem value="0.5">0.5</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={6}>
                    <FormControl fullWidth>
                      <InputLabel>β_z (NCCI 1 Table 9)</InputLabel>
                      <Select
                        value={fireSettings.beta_z === '' || fireSettings.beta_z == null ? 'same' : String(fireSettings.beta_z)}
                        label="β_z (NCCI 1 Table 9)"
                        onChange={(e) => setFireSettings({
                          ...fireSettings,
                          beta_z: e.target.value === 'same' ? '' : parseFloat(e.target.value),
                        })}
                      >
                        <MenuItem value="same">Same as β</MenuItem>
                        <MenuItem value="1">1.0</MenuItem>
                        <MenuItem value="0.7">0.7</MenuItem>
                        <MenuItem value="0.6">0.6</MenuItem>
                        <MenuItem value="0.5">0.5</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <InputLabel>Fire buckling length β (NCCI 1 Table 9)</InputLabel>
                      <Select
                        value={['0.5', '0.6', '0.7', '1'].includes(
                          Number(fireSettings.k_L_modifier) === 1
                            ? '1'
                            : String(fireSettings.k_L_modifier)
                        )
                          ? (Number(fireSettings.k_L_modifier) === 1 ? '1' : String(fireSettings.k_L_modifier))
                          : '1'}
                        label="Fire buckling length β (NCCI 1 Table 9)"
                        onChange={(e) => setFireSettings({
                          ...fireSettings,
                          k_L_modifier: parseFloat(e.target.value),
                        })}
                      >
                        <MenuItem value="1">Single storey — β same as ambient (1.0)</MenuItem>
                        <MenuItem value="0.7">Top storey, continuous column — β = 0.7</MenuItem>
                        <MenuItem value="0.6">Bottom storey, continuous column — β = 0.6</MenuItem>
                        <MenuItem value="0.5">Intermediate storey — β = 0.5</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Temperatures for steel / concrete / rebar (fire check)
                    </Typography>
                    <RadioGroup
                      row
                      value={fireSettings.fire_temperature_source || 'ncci_tables'}
                      onChange={(e) => setFireSettings({ ...fireSettings, fire_temperature_source: e.target.value })}
                    >
                      <FormControlLabel
                        value="ncci_tables"
                        control={<Radio size="small" />}
                        label="NCCI tables (default)"
                      />
                      <FormControlLabel
                        value="thermal_fem_2d"
                        control={<Radio size="small" />}
                        label="2D transient FEM (ISO 834)"
                      />
                    </RadioGroup>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                      FEM uses the same Finnish NA buckling / interaction; φ_a and φ_s remain from NCCI tables.
                      Results include a cross-section temperature contour when FEM is selected.
                    </Typography>
                  </Grid>
                  {fireSettings.fire_temperature_source === 'thermal_fem_2d' && (
                    <>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="FEM time step Δt (s)"
                          type="number"
                          value={fireSettings.thermal_fem_dt_seconds}
                          onChange={(e) => setFireSettings({
                            ...fireSettings,
                            thermal_fem_dt_seconds: parseFlexibleFloat(e.target.value),
                          })}
                        />
                      </Grid>
                      {sectionConfig.comcol_section_type === 'CHS' ? (
                        <Grid item xs={12}>
                          <Typography variant="body2" color="text.secondary">
                            CHS uses the built-in polar thermal mesh (radial / circumferential refinement).
                          </Typography>
                        </Grid>
                      ) : (
                        <>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              label="FEM mesh size (mm)"
                              type="number"
                              value={fireSettings.thermal_fem_element_size_mm}
                              onChange={(e) => setFireSettings({
                                ...fireSettings,
                                thermal_fem_element_size_mm: parseFlexibleFloat(e.target.value),
                              })}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <FormControlLabel
                              control={(
                                <Checkbox
                                  checked={fireSettings.thermal_fem_wall_aligned_mesh !== false}
                                  onChange={(e) => setFireSettings({
                                    ...fireSettings,
                                    thermal_fem_wall_aligned_mesh: e.target.checked,
                                  })}
                                />
                              )}
                              label="Wall-aligned mesh (recommended for RHS weld corners)"
                            />
                          </Grid>
                        </>
                      )}
                    </>
                  )}
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        )}

        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={6}>
                  <Button
                    variant="outlined"
                    startIcon={<Save />}
                    onClick={() => saveFormData({
                      projectData,
                      sectionConfig,
                      loads,
                      fireSettings,
                      useAdvancedLoads,
                      consequenceClass,
                      loadCases,
                    })}
                  >
                    Save locally (browser)
                  </Button>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Button
                    variant="outlined"
                    startIcon={<FolderOpen />}
                    onClick={() => {
                      const data = loadFormData();
                      if (data) {
                        setProjectData(data.projectData || projectData);
                        setSectionConfig(data.sectionConfig || sectionConfig);
                        setLoads(data.loads || loads);
                        setFireSettings(data.fireSettings || fireSettings);
                        setUseAdvancedLoads(data.useAdvancedLoads || false);
                        setConsequenceClass(data.consequenceClass || 'CC2');
                        setLoadCases(data.loadCases || []);
                      }
                    }}
                  >
                    Load locally (browser)
                  </Button>
                </Grid>
                <Grid item xs={12}>
                  <Button
                    variant="outlined"
                    startIcon={<Download />}
                    onClick={() => {
                      const data = {
                        projectData,
                        sectionConfig,
                        loads,
                        fireSettings,
                        useAdvancedLoads,
                        consequenceClass,
                        loadCases,
                      };
                      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = 'composite-column-config.json';
                      a.click();
                      URL.revokeObjectURL(url);
                    }}
                  >
                    Export Configuration
                  </Button>
                </Grid>
                <Grid item xs={12}>
                  <Button
                    variant="contained"
                    size="large"
                    startIcon={loading ? <CircularProgress size={20} /> : <Calculate />}
                    onClick={handleRunAnalysis}
                    disabled={loading}
                    sx={{ minWidth: 200, py: 1.5 }}
                  >
                    {loading ? 'Running Analysis...' : 'Run Composite Column Analysis'}
                  </Button>
                </Grid>

                {error && (
                  <Alert severity="error" sx={{ mt: 2 }}>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                      Please fix the following issues:
                      {'\n• ' + error}
                    </Typography>
                  </Alert>
                )}
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </div>
  );

};

export default CompositeColumnInputForm;